import { useTranslation } from "react-i18next";

import { type DocumentSummary } from "~/api/types";
import { useDocument } from "~/api/v2/useDocument";
import { Label } from "~/atoms/label";
import { CenterSpinner, Spinner } from "~/atoms/spinner";

import { ControlledCombobox } from "./ControlledCombobox";
import ErrorState from "./ErrorState";

interface DocumentFileSourceSelection {
  documentId?: string;
  releaseId?: string;
}

interface Props {
  documents?: DocumentSummary[];
  error?: Error;
  isLoading: boolean;
  onRetry: () => void;
  value?: DocumentFileSourceSelection;
  onChange: (value: DocumentFileSourceSelection) => void;
}

export function DocumentFileSourcePicker({
  documents,
  error,
  isLoading,
  onRetry,
  value,
  onChange,
}: Props) {
  const { t } = useTranslation();
  const sourceDocument = useDocument(value?.documentId);

  if (error) {
    return <ErrorState onRetry={onRetry} />;
  }

  if (isLoading || !documents) {
    return <CenterSpinner withText />;
  }

  if (documents.length === 0) {
    return null;
  }

  const documentOptions = documents
    .map((document) => ({
      value: String(document.id),
      label: document.name,
      subtitle: document.category?.title,
    }))
    .toSorted((a, b) => a.label.localeCompare(b.label));

  const releaseOptions = sourceDocument.data?.releases
    .filter((release) => release.artifact.permissions.canRead)
    .toSorted(
      (a, b) =>
        b.majorVersion - a.majorVersion || b.minorVersion - a.minorVersion,
    )
    .map((release) => ({
      value: String(release.id),
      label: `${t(($) => $.commons.workflow.versionOrder, {
        order:
          release.minorVersion > 0
            ? `${release.majorVersion}.${release.minorVersion}`
            : release.majorVersion,
      })} - ${release.artifact.title}`,
    }));

  return (
    <div className="grid min-w-0 gap-3">
      <div className="grid min-w-0 gap-2">
        <Label>{t(($) => $.dashboard.createDocument.sourceDocument)}</Label>
        <ControlledCombobox
          value={value?.documentId}
          onChange={(documentId) => {
            if (documentId !== value?.documentId) onChange({ documentId });
          }}
          options={documentOptions}
          placeholder={t(
            ($) => $.dashboard.createDocument.searchSourceDocument,
          )}
        />
      </div>
      {value?.documentId && (
        <div className="grid min-w-0 gap-2">
          <Label>{t(($) => $.dashboard.createDocument.sourceVersion)}</Label>
          {sourceDocument.error ? (
            <ErrorState
              onRetry={() =>
                void sourceDocument.mutate().catch(() => undefined)
              }
            />
          ) : !sourceDocument.data ? (
            <CenterSpinner withText />
          ) : (
            <div className="flex min-w-0 items-center gap-2">
              <ControlledCombobox
                value={value.releaseId}
                onChange={(releaseId) =>
                  onChange({
                    documentId: value.documentId,
                    releaseId,
                  })
                }
                options={releaseOptions}
                placeholder={t(
                  ($) => $.dashboard.createDocument.selectSourceVersion,
                )}
              />
              {sourceDocument.isValidating && (
                <Spinner className="size-4 shrink-0" />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
